/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Button,
  Loader,
  Message,
  Segment,
  Sidebar,
} from '@mairistem/composants';
import { useHubeeRetour } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { DEFAULT_PACS, ASSISTANT_STEPS } from '@/constants';
import { Steps } from '@/enums';
// import { base64ToUint8Array, renderPdfPageToDataURL } from '@/utils/pdf-helpers';
import { AssistantContext } from '@/contexts';
// import { useCreateIndividu, useUpdateIndividu } from '@/hooks';

import {
  validationStep1,
  validationStep2,
  validationStep3,
  validationStep4et6,
  validationStep5et7,
  validationStep8,
} from '@/utils/validations';

// import { Individu } from '@/types';
import { composeLibellesVoieConvention, computeNextPacsNumber } from '@/utils';
import { useRegistreStore } from '@/zustand';
import {
  baseUrlApp,
  getLastRegistre,
  getHubeeTraiterTeledossier,
  getNumeroLastPacs,
} from '@/routes';
import { useHubeeTeledossier } from '@/hooks';
import AssistantModalFin from './AssistantModalFin';
import SidebarRecapitulatif from './SidebarRecapitulatif';
import AssistantFooter from './AssistantFooter';
import AssistantHeader from './AssistantHeader';
import AssistantModalErrorStepFin from './AssistantModalErrorStepFin';
import AssistantModalFinTraitementHubee from './AssistantModalFinTraitementHubee';
import AssistantModalErrorStep from './AssistantModalErrorStep';
import AssistantModalSansPrenom from './AssistantModalSansPrenom';
import AssistantModalError from './AssistantModalError';
import AssistantModalErrorCreation from './AssistantModalErrorCreation';
import ConfirmationModal from './ConfirmationModal';
import { StepPartenaire } from './StepPartenaire';
import { StepParentsPartenaire } from './StepParentsPartenaire';
import { StepTypeDeclaration } from './StepTypeDeclaration';
import { StepSelectionPACS } from './StepSelectionPACS';
import { StepDeclarationConvention } from './StepDeclarationConvention';
import { StepPACS } from './StepPACS';

/**
 * Étapes masquées en mode dissolution (partenaires, parents et
 * déclaration/convention ne sont pas saisis pour une dissolution).
 * Utilisé à la fois pour l'affichage (header/footer) et pour la validation,
 * afin de ne pas vérifier des champs jamais affichés.
 */
const DISSOLUTION_HIDDEN_STEPS: Steps[] = [
  Steps.Partenaire1,
  Steps.ParentsPartenaire1,
  Steps.Partenaire2,
  Steps.ParentsPartenaire2,
  Steps.DeclarationConvention,
];

/**
 * Étapes masquées en mode Hubee : une demande dématérialisée est toujours une
 * création de projet de PACS, le type de déclaration et la sélection du PACS
 * sont donc déjà connus (cf. `useHubeeTeledossier`).
 */
const HUBEE_HIDDEN_STEPS: Steps[] = [
  Steps.TypeDeclaration,
  Steps.SelectionPACS,
];

const Assistant = () => {
  // #region Declarations

  // Mode Hubee : identifiantTeledossier présent dans l'URL, données du
  // télédossier chargées dans le contexte pour préremplir les champs
  const {
    isHubeeMode,
    identifiantTeledossier,
    isTeledossierCharge,
    hasErreurTeledossier,
  } = useHubeeTeledossier();
  const { navigateBack } = useHubeeRetour();

  /** Dernier registre reçu pour le type de déclaration courant */
  const [isRegistreCharge, setIsRegistreCharge] = React.useState(false);

  /** Index de l'étape actuelle */
  const [currentStepIndex, setCurrentStepIndex] = React.useState<Steps>(
    // En mode Hubee, on démarre sur la saisie du PACS : les deux premières
    // étapes sont masquées
    isHubeeMode ? Steps.PACS : Steps.TypeDeclaration,
  );
  /** Modal de fin ouverte */
  const [showModalFin, setShowModalFin] = React.useState<number>();
  const [showModalError, setShowModalError] = React.useState(false);
  const [showModalErrorCreation, setShowModalErrorCreation] =
    React.useState(false);
  /**
   * Numéro corrigé (dernier numéro du registre + 1) en attente de création :
   * en numérotation manuelle, on prévient l'utilisateur que son numéro était
   * déjà attribué, puis la création est lancée à la fermeture de la modal.
   */
  const [numeroEnAttenteCreation, setNumeroEnAttenteCreation] = React.useState<
    string | null
  >(null);
  /** Numéro saisi déjà attribué, affiché dans la modal d'erreur de création */
  const [numeroDejaAttribue, setNumeroDejaAttribue] = React.useState<
    string | null
  >(null);
  const [loading, setLoading] = React.useState(false);
  /** Modal d'envoi attestation en mode Hubee */
  const [showModalEnvoieAttestation] = React.useState<number>();
  /** Confirmation d'erreur sur la step, on stocke l'identifiant de la next step si validation */
  const [showModalErrorStep, setShowModalErrorStep] = React.useState<Steps>();
  // Étape visée et partenaires sans prénom à confirmer avant d'y aller
  const [modalSansPrenom, setModalSansPrenom] = React.useState<{
    stepIndex: Steps;
    partenaires: number[];
  }>(null);
  /** Modal d'erreur de saisie globale avant d'envoyer au back */
  /** Modal de confirmation de fin de traitement (mode Hubee) */
  const [showModalFinTraitementHubee, setShowModalFinTraitementHubee] =
    React.useState(false);
  const [showModalErrorStepFin, setShowModalErrorStepFin] =
    React.useState<boolean>(false);
  /**
   * Confirmation de retour sur l'étape de type de déclaration ou de sélection
   * (perte des données saisies non enregistré)
   */
  const [showModalRetourEtape, setShowModalRetourEtape] =
    React.useState<Steps>();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState(null);
  const [navigationPath, setNavigationPath] = React.useState(null);
  // Ajout d'un état pour bloquer le Prompt pendant la navigation
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [updated, setUpdated] = React.useState<boolean>(false);

  const setRegistre = useRegistreStore((state) => state.setRegistre);
  const history = useHistory();
  const context = React.useContext(AssistantContext);
  const { isDissolutionMode, teledossierData } = context;

  const pacs = React.useContext(AssistantContext);
  const {
    errors,
    setErrors,
    onReset,
    resetPacsNumero,
    setPacsNumero,
    isNumeroSaisiManuellement,
    selectedDeclarationType,
    selectedPacs,
  } = pacs || {};

  /** Étapes non affichées, donc ni parcourues ni validées */
  const hiddenSteps = React.useMemo(() => {
    if (isHubeeMode) return HUBEE_HIDDEN_STEPS;

    return isDissolutionMode ? DISSOLUTION_HIDDEN_STEPS : [];
  }, [isHubeeMode, isDissolutionMode]);

  const steps = _.isEmpty(hiddenSteps)
    ? ASSISTANT_STEPS
    : _.omit(ASSISTANT_STEPS, hiddenSteps);

  // Hook pour créer un individu
  // const { createIndividu } = useCreateIndividu();
  // Hook pour mettre à jour un individu brouillon
  // const { updateIndividuBrouillon } = useUpdateIndividu();

  // État pour la première page du PDF 10870-PDF
  // const [pdfFirstPageUrl] = React.useState<string | null>(null);
  // const [pdfLoading] = React.useState<boolean>(false);

  // Fonction pour extraire et afficher le PDF depuis les données
  // const handlePdfExtraction = React.useCallback((data: any) => {
  //   // Cas 1: Données Hubee (piecesJointes)
  //   let pdfBinary = data?.piecesJointes?.find(
  //     (pj: any) => pj.typeHubee === '10870-PDF',
  //   )?.pieceJointeBinaire?.binary;

  //   // Cas 2: Individu brouillon (notice array)
  //   if (!pdfBinary && !_.isNil(data?.notice)) {
  //     pdfBinary = data.notice?.pieceJointeBinaire?.binary;
  //   }

  //   if (pdfBinary) {
  //     setPdfLoading(true);
  //     try {
  //       const pdfData = base64ToUint8Array(pdfBinary);
  //       renderPdfPageToDataURL(pdfData, 1, 800)
  //         .then((dataUrl) => {
  //           setPdfFirstPageUrl(dataUrl);
  //         })
  //         .catch(() => {})
  //         .finally(() => {
  //           setPdfLoading(false);
  //         });
  //     } catch (e) {
  //       setPdfLoading(false);
  //     }
  //   }
  // }, []);

  // // Vérifie d'abord si un brouillon existe pour ce télédossier
  // React.useEffect(() => {
  //   // Récupérer l'identifiant du télédossier depuis les paramètres de l'URL
  //   const { search } = history?.location || {};
  //   const teledossierId = search?.split('identifiantTeledossier=')[1];
  //   setIsHubeeMode(!!teledossierId);
  //   setIdentifiantTeledossier(teledossierId || null);

  //   if (teledossierId) {
  //     if (setIsLoadingHubeeData) {
  //       setIsLoadingHubeeData(true);
  //     }

  //     // La route retourne soit l'individu brouillon s'il existe,
  //     // soit les données du télédossier Hubee sinon
  //     request.get(`/api/administres/recensement-citoyen/individus/teledossiers/${teledossierId}`)
  //       .then((response: any) => {
  //         // Détecter si c'est un individu brouillon (a un identifiant et isBrouillon pas null)
  //         // ou des données Hubee (a demandeur structuré)
  //         const isExistingBrouillon = response?.identifiant && !_.isNil(response?.isBrouillon);

  //         if (isExistingBrouillon) {
  //           // C'est un individu brouillon existant
  //           setIndividuBrouillonId(response.identifiant);

  //           // Passer les données du brouillon au provider
  //           if (setTeledossierData) {
  //             setTeledossierData({
  //               isExistingBrouillon: true,
  //               individu: response,
  //             });
  //           }

  //           handlePdfExtraction(response);

  //           if (setIsLoadingHubeeData) {
  //             setIsLoadingHubeeData(false);
  //           }
  //         } else {
  //           // C'est un nouveau télédossier au format Hubee
  //           if (setTeledossierData) {
  //             setTeledossierData(response);
  //           }
  //           handlePdfExtraction(response);
  //         }
  //       })
  //       .catch(() => {
  //         if (setIsLoadingHubeeData) {
  //           setIsLoadingHubeeData(false);
  //         }
  //       });
  //   }
  // }, [setTeledossierData, setIsLoadingHubeeData, history.location, handlePdfExtraction]);

  // Effet pour gérer la navigation
  React.useEffect(() => {
    if (navigationPath && isNavigating) {
      history.push(navigationPath);
      setNavigationPath(null);
    }
  }, [navigationPath, isNavigating, history]);

  React.useEffect(() => {
    if (
      !_.isEqual(
        _.omit(pacs, [
          'errors',
          'setErrors',
          'onChange',
          'computedPacsNumber',
          'anneeRecensement',
          'trimestreRecensement',
          'dateRecensement',
          'adresseContact',
          'onReset',
        ]),
        _.omit(DEFAULT_PACS, [
          'anneeRecensement',
          'trimestreRecensement',
          'dateRecensement',
          'adresseContact',
        ]),
      )
    ) {
      setUpdated(true);
    } else {
      setUpdated(false);
    }
  }, [pacs]);

  // #region Functions

  const handleErrors = React.useCallback(
    (current: Steps, stepIndex: Steps) => {
      let hasErrors = false;
      const newErrors = {};

      // On vérifie toutes les étapes de currentStepIndex jusqu'à stepIndex
      // eslint-disable-next-line no-plusplus
      for (let step = current; step <= stepIndex - 1; step++) {
        // On ne valide pas les étapes masquées : celles de la dissolution
        // (partenaires, parents, déclaration/convention) comme celles du mode
        // Hubee (type de déclaration, sélection du PACS)
        if (hiddenSteps.includes(step)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        let stepErrors = null;
        switch (step) {
          case Steps.TypeDeclaration:
            stepErrors = validationStep1(selectedDeclarationType);
            break;
          case Steps.SelectionPACS:
            stepErrors = validationStep2(selectedPacs);
            break;
          case Steps.PACS:
            stepErrors = validationStep3(pacs, selectedDeclarationType);
            break;
          case Steps.Partenaire1:
            stepErrors = validationStep4et6(pacs, 1, selectedDeclarationType);
            break;
          case Steps.ParentsPartenaire1:
            // Saisie facultative : on ne valide un parent que s'il est amorcé
            stepErrors = validationStep5et7(pacs, 1);
            break;
          case Steps.Partenaire2:
            stepErrors = validationStep4et6(pacs, 2, selectedDeclarationType);
            break;
          case Steps.ParentsPartenaire2:
            // Saisie facultative : on ne valide un parent que s'il est amorcé
            stepErrors = validationStep5et7(pacs, 2);
            break;
          case Steps.DeclarationConvention:
            stepErrors = validationStep8(pacs, selectedDeclarationType);
            break;
          default:
            break;
        }
        if (!_.isEmpty(stepErrors)) {
          hasErrors = true;
        }
        newErrors[step] = stepErrors;
      }

      setErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));

      return hasErrors;
    },
    [selectedPacs, selectedDeclarationType, setErrors, pacs, hiddenSteps],
  );

  /**
   * Applique un retour en arrière sur une étape précédente (sans validation).
   * Extrait de handleStepChange pour être rejouable après confirmation de la
   * modal de retour d'étape.
   */
  const applyStepRetour = React.useCallback(
    (stepIndex: Steps) => {
      // Revenir sur le type de déclaration ou la sélection réinitialise la
      // saisie et désélectionne le PACS
      if (
        stepIndex === Steps.TypeDeclaration ||
        stepIndex === Steps.SelectionPACS
      ) {
        onReset({ keepDeclarationType: true });
      }

      setCurrentStepIndex(stepIndex);
    },
    [onReset],
  );

  /**
   * Avance jusqu'à stepIndex. Le prénom d'un partenaire est facultatif (une
   * personne peut ne pas en avoir) : si une étape partenaire franchie n'en a
   * pas, on demande d'abord confirmation.
   */
  const avancerVersStep = React.useCallback(
    (stepIndex: Steps) => {
      const partenaires = [
        { step: Steps.Partenaire1, numero: 1 },
        { step: Steps.Partenaire2, numero: 2 },
      ]
        .filter(
          ({ step, numero }) =>
            step >= currentStepIndex &&
            step < stepIndex &&
            !hiddenSteps.includes(step) &&
            _.isEmpty(
              _.trim(
                pacs?.conventionDeclaration?.[`partenaire${numero}`]?.prenom,
              ),
            ),
        )
        .map(({ numero }) => numero);

      if (_.isEmpty(partenaires)) {
        setCurrentStepIndex(stepIndex);
      } else {
        setModalSansPrenom({ stepIndex, partenaires });
      }
    },
    [currentStepIndex, hiddenSteps, pacs?.conventionDeclaration],
  );

  /**
   * Callback appelé lors du changement de step
   */
  const handleStepChange = React.useCallback(
    (stepIndex: Steps) => {
      // Si on recule, on change directement d'étape sans validation
      if (stepIndex < currentStepIndex) {
        // Revenir sur le type de déclaration ou la sélection du PACS depuis une
        // étape de saisie réinitialise le formulaire : on demande confirmation
        if (
          stepIndex <= Steps.SelectionPACS &&
          currentStepIndex > Steps.SelectionPACS
        ) {
          setShowModalRetourEtape(stepIndex);
          return;
        }

        applyStepRetour(stepIndex);
        return;
      }

      const hasErrors = handleErrors(currentStepIndex, stepIndex);

      // Si on a des erreurs on affiche la modal de confirmation
      if (hasErrors) {
        setShowModalErrorStep(stepIndex);
      } else {
        avancerVersStep(stepIndex);
      }
    },
    [currentStepIndex, handleErrors, applyStepRetour, avancerVersStep],
  );

  const handleValidateModalRetourEtape = React.useCallback(() => {
    applyStepRetour(showModalRetourEtape as Steps);
    setShowModalRetourEtape(undefined);
  }, [applyStepRetour, showModalRetourEtape]);

  // Passage à l'étape suivante lors de la sélection d'un type de déclaration
  const handleTypeDeclarationSelect = React.useCallback(() => {
    setCurrentStepIndex(Steps.SelectionPACS);
  }, []);

  const handleValidateModalErrorStep = React.useCallback(() => {
    avancerVersStep(showModalErrorStep as Steps);
    setShowModalErrorStep(null);
  }, [showModalErrorStep, avancerVersStep]);

  const handleValidateModalSansPrenom = React.useCallback(() => {
    setCurrentStepIndex(modalSansPrenom?.stepIndex);
    setModalSansPrenom(null);
  }, [modalSansPrenom]);

  const handleValidateModalErrorStepFin = React.useCallback(() => {
    // On trouve le premier index qui a des erreurs non vides
    const stepToGo = Object.keys(errors).find((key) => !_.isEmpty(errors[key]));
    const stepToDisplay = parseInt(stepToGo as string, 10) as Steps;
    setCurrentStepIndex(stepToDisplay);
    setShowModalErrorStepFin(false);
  }, [errors]);

  const handleValidate = React.useCallback(
    (numeroOverride?: string) => {
      // vérification du numéro de pacs est côté back
      setLoading(true);

      const payload = {
        ..._.pick(pacs, Object.keys(DEFAULT_PACS)),
        conventionDeclaration: composeLibellesVoieConvention(
          pacs?.conventionDeclaration,
        ),
        // le contexte n'est pas encore rafraîchi quand le numéro vient d'être
        // corrigé juste avant l'envoi (cf. handleFinish)
        ...(_.isNil(numeroOverride) ? {} : { numero: numeroOverride }),
        selectedDeclarationType,
      };

      request
        .post('/api/administres/etat-civil/pacs/creation', payload)
        .then((response: any) => {
          // Hors mode Hubee : modal de fin classique
          if (!identifiantTeledossier) {
            setShowModalFin(response.identifiant);
            return undefined;
          }

          // Mode Hubee : le PACS créé, on marque la demande dématérialisée comme
          // traitée, puis on revient d'où l'on vient (liste ou fiche demande).
          return request
            .put(getHubeeTraiterTeledossier(identifiantTeledossier), {
              identifiantDossierPacs: response.identifiant,
            })
            .catch(() => {
              // Le PACS est bien créé : on prévient sans bloquer le retour, la
              // demande restera à traiter côté Hubee.
              toast.error(
                "Le PACS a été créé mais la demande Hubee n'a pas pu être marquée comme traitée",
              );
            })
            .finally(() => {
              // Évite la modal de confirmation de sortie de l'assistant
              setIsNavigating(true);
              navigateBack(
                `${baseUrlApp}hubee`,
                `${baseUrlApp}hubee/${identifiantTeledossier}`,
              );
            });
        })
        .catch(() => {
          setNumeroDejaAttribue(payload.numero);
          setShowModalErrorCreation(true);
          resetPacsNumero();
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      pacs,
      selectedDeclarationType,
      resetPacsNumero,
      identifiantTeledossier,
      navigateBack,
    ],
  );

  /**
   * Récupère le dernier numéro de PACS enregistré sur le registre sélectionné.
   * Renvoie null si le registre est incomplet -> création se lance avec numéro actuel
   */
  const fetchNumeroLastPacs = React.useCallback(async () => {
    const registrePacs = pacs?.registre;
    const anneeRegistre = registrePacs?.annee?.annee;
    const numeroRegistre = registrePacs?.numero;
    const typeRegistre = registrePacs?.type;

    if (
      _.isNil(anneeRegistre) ||
      _.isNil(numeroRegistre) ||
      _.isNil(typeRegistre)
    ) {
      return null;
    }

    try {
      const response: any = await request.get(
        getNumeroLastPacs(anneeRegistre, numeroRegistre, typeRegistre),
      );

      // la route peut renvoyer le numéro brut ou un objet l'encapsulant
      // selon si on le récupère via /pacs/liste ou via /registre/numero-last-pacs
      const numeroLastPacs = _.isPlainObject(response)
        ? (_.get(response, 'numeroLastPacs') ?? _.get(response, 'numero'))
        : response;

      return _.isNil(numeroLastPacs) || numeroLastPacs === ''
        ? null
        : String(numeroLastPacs);
    } catch {
      return null;
    }
  }, [pacs?.registre]);

  /**
   * Lance la création, une fois les étapes validées (et, en mode Hubee, la
   * fin de traitement confirmée).
   */
  const lancerCreation = React.useCallback(async () => {
    const isReprisePacs = !_.isNil(selectedPacs) && selectedPacs !== -1;
    const isConversionProjetInitiale =
      isReprisePacs && selectedDeclarationType === 'initiale';
    const skipVerificationNumero = isReprisePacs && !isConversionProjetInitiale;

    if (skipVerificationNumero) {
      handleValidate();
      return;
    }

    // Le registre a pu être alimenté entre-temps (autre poste) : on repart du
    // dernier numéro de PACS réellement enregistré avant de créer.
    setLoading(true);
    const numeroLastPacs = await fetchNumeroLastPacs();
    setLoading(false);

    const numeroCourant = pacs?.numero;

    // Numéro reçu inférieur au numéro en cours (ou indisponible) : rien à faire
    if (
      _.isNil(numeroLastPacs) ||
      _.isNil(numeroCourant) ||
      numeroCourant === '' ||
      Number(numeroCourant) > Number(numeroLastPacs)
    ) {
      handleValidate();
      return;
    }

    // Sinon le numéro en cours est déjà attribué : on prend celui reçu + 1
    const numeroCorrige = computeNextPacsNumber({ numeroLastPacs });

    if (_.isNil(numeroCorrige)) {
      handleValidate();
      return;
    }

    setPacsNumero(numeroCorrige);

    // En numérotation manuelle, si l'utilisateur avait saisi un numéro, on
    // l'avertit de la correction : la création est lancée à la fermeture de la
    // modal. Sinon (numéro généré automatiquement), elle se poursuit
    // directement.
    const isNumerotationManuelle = pacs?.registre?.numerotationManuelle === 1;

    if (isNumerotationManuelle && isNumeroSaisiManuellement) {
      setNumeroDejaAttribue(numeroCourant);
      setNumeroEnAttenteCreation(numeroCorrige);
      setShowModalErrorCreation(true);
      return;
    }

    handleValidate(numeroCorrige);
  }, [
    handleValidate,
    fetchNumeroLastPacs,
    pacs?.numero,
    pacs?.registre?.numerotationManuelle,
    isNumeroSaisiManuellement,
    setPacsNumero,
    selectedPacs,
    selectedDeclarationType,
  ]);

  /**
   * Callback appelé lors du clic sur le bouton "Terminer"
   */
  const handleFinish = React.useCallback(() => {
    const hasErrors = handleErrors(
      Steps.TypeDeclaration,
      Steps.DeclarationConvention + 1,
    );

    if (hasErrors) {
      setShowModalErrorStepFin(true);
      return;
    }

    // Mode Hubee : confirmation de fin de traitement, comme dans RCO
    if (isHubeeMode) {
      setShowModalFinTraitementHubee(true);
      return;
    }

    lancerCreation();
  }, [handleErrors, isHubeeMode, lancerCreation]);

  const handleConfirmFinTraitementHubee = React.useCallback(() => {
    setShowModalFinTraitementHubee(false);
    lancerCreation();
  }, [lancerCreation]);

  const handleCloseModalErrorCreation = React.useCallback(() => {
    setShowModalErrorCreation(false);
    setNumeroDejaAttribue(null);

    if (_.isNil(numeroEnAttenteCreation)) {
      return;
    }

    setNumeroEnAttenteCreation(null);
    handleValidate(numeroEnAttenteCreation);
  }, [numeroEnAttenteCreation, handleValidate]);

  const handleBlockNavigation = React.useCallback(
    (location) => {
      if (loading || isNavigating || !updated) return false;

      setPendingNavigation(location);
      setShowConfirmModal(true);
      return false;
    },
    [loading, isNavigating, updated],
  );

  const handleConfirmNavigation = React.useCallback(() => {
    if (pendingNavigation) {
      setNavigationPath(pendingNavigation.pathname);
      setIsNavigating(true);
      setPendingNavigation(null);
      setShowConfirmModal(false);
    }
  }, [pendingNavigation]);

  /**
   * Callback appelé à la fin du processus de la modal d'envoi d'attestation
   * (que l'utilisateur clique sur Oui ou Non)
   */
  // const handleModalEnvoieAttestation = React.useCallback(() => {
  //   // On ferme la modal et on redirige vers l'accueil ou la liste
  //   setShowModalEnvoieAttestation(undefined);
  //   history.push(baseUrlApp);
  // }, [history]);

  const handleResetAssistant = React.useCallback(() => {
    setIsNavigating(false);
    setUpdated(false);
    setCurrentStepIndex(0);
    setShowModalErrorStep(null);
    setModalSansPrenom(null);
    setShowModalErrorStepFin(false);
    setShowModalRetourEtape(undefined);
    setPendingNavigation(null);
    setNavigationPath(null);
    setLoading(false);
    setShowModalFin(null);
    setShowModalErrorCreation(false);
    setNumeroEnAttenteCreation(null);
    onReset();
  }, [onReset]);

  // Récupère le dernier registre dès qu'un type de déclaration est sélectionné.
  React.useEffect(() => {
    if (_.isNil(selectedDeclarationType)) {
      return;
    }

    setIsRegistreCharge(false);

    request
      .get(getLastRegistre(selectedDeclarationType === 'projet' ? 10 : 11))
      .then((response: any) => {
        setRegistre(response);
      })
      .finally(() => {
        setIsRegistreCharge(true);
      });
  }, [selectedDeclarationType, setRegistre]);

  // Mode Hubee : l'assistant n'est affiché qu'une fois le télédossier et le
  // registre reçus. Chacun relance l'initialisation du PACS : une saisie faite
  // avant leur arrivée serait écrasée.
  const isHubeeEnAttente =
    isHubeeMode && !(isTeledossierCharge && isRegistreCharge);

  const handleRetourDemandesHubee = React.useCallback(() => {
    setIsNavigating(true);
    navigateBack(
      `${baseUrlApp}hubee`,
      `${baseUrlApp}hubee/${identifiantTeledossier}`,
    );
  }, [identifiantTeledossier, navigateBack]);

  // #endregion

  // #region Components

  const stepToDisplay = React.useMemo(() => {
    switch (currentStepIndex) {
      case Steps.TypeDeclaration:
        return <StepTypeDeclaration onSelect={handleTypeDeclarationSelect} />;

      case Steps.SelectionPACS:
        return <StepSelectionPACS />;

      case Steps.PACS:
        return <StepPACS />;

      case Steps.Partenaire1:
        // key sur stepPartenaire pour forcer re-render : évite que les données partenaire 1 et 2
        // soient mélangées (erreurs de validation)
        return <StepPartenaire key="partenaire-1" numeroPartenaire={1} />;

      case Steps.ParentsPartenaire1:
        return (
          <StepParentsPartenaire
            key="parents-partenaire-1"
            numeroPartenaire={1}
          />
        );

      case Steps.Partenaire2:
        return <StepPartenaire key="partenaire-2" numeroPartenaire={2} />;

      case Steps.ParentsPartenaire2:
        return (
          <StepParentsPartenaire
            key="parents-partenaire-2"
            numeroPartenaire={2}
          />
        );

      case Steps.DeclarationConvention:
        return <StepDeclarationConvention />;

      default:
        return <h1>Une erreur est survenue...</h1>;
    }
  }, [currentStepIndex, handleTypeDeclarationSelect]);
  return (
    <>
      <Prompt
        when={
          !loading &&
          !isNavigating &&
          updated &&
          // Modal ne s'affiche qu'a partir de l'étape 3 : en dessous, aucune données saisies
          currentStepIndex >= Steps.PACS &&
          _.isNil(showModalFin) &&
          _.isNil(showModalEnvoieAttestation)
        }
        message={handleBlockNavigation}
      />
      <Sidebar.Pushable
        style={{ height: '100%', overflow: 'hidden', position: 'relative' }}
      >
        {isHubeeMode && !isHubeeEnAttente && identifiantTeledossier && (
          <SidebarRecapitulatif
            identifiantTeledossier={identifiantTeledossier}
            teledossier={teledossierData}
          />
        )}
        <Sidebar.Pusher>
          <div
            style={{
              height: '100%',
              padding: '1em',
            }}
          >
            <Segment
              raised
              style={{
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: 'white',
                borderRadius: '16px',
              }}
            >
              {isHubeeEnAttente ? (
                <div
                  data-testid="assistant-hubee-attente"
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1em',
                    padding: '1em',
                  }}
                >
                  {hasErreurTeledossier ? (
                    <>
                      <Message
                        negative
                        header="Demande en ligne indisponible"
                        content="La demande n'a pas pu être chargée : le PACS ne peut pas être créé à partir de celle-ci pour le moment."
                      />
                      <Button
                        content="Retour"
                        icon="arrow left"
                        onClick={handleRetourDemandesHubee}
                      />
                    </>
                  ) : (
                    <Loader active inline="centered">
                      Chargement de la demande en ligne...
                    </Loader>
                  )}
                </div>
              ) : (
                <>
                  <AssistantHeader
                    currentStepIndex={currentStepIndex}
                    onStepChange={handleStepChange}
                    steps={steps}
                  />
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      overflowY: 'auto',
                      scrollbarGutter: 'stable',
                      padding: '1em 1em',
                    }}
                  >
                    {stepToDisplay}
                  </div>
                  <AssistantFooter
                    currentStepIndex={currentStepIndex}
                    onStepChange={handleStepChange}
                    onFinish={handleFinish}
                    loading={loading}
                    steps={steps}
                  />
                </>
              )}
            </Segment>
          </div>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
      {!_.isNil(showModalFin) && (
        <AssistantModalFin
          identifiant={showModalFin}
          onClose={() => setShowModalFin(null)}
          onReset={handleResetAssistant}
        />
      )}
      {showModalError && (
        <AssistantModalError
          isOpen={showModalError}
          onClose={() => setShowModalError(false)}
        />
      )}
      {showModalErrorCreation && (
        <AssistantModalErrorCreation
          numero={numeroDejaAttribue}
          onClose={handleCloseModalErrorCreation}
        />
      )}
      {showModalErrorStep && (
        <AssistantModalErrorStep
          onClose={() => setShowModalErrorStep(null)}
          onValidate={handleValidateModalErrorStep}
        />
      )}
      {modalSansPrenom && (
        <AssistantModalSansPrenom
          partenaires={modalSansPrenom.partenaires}
          onClose={() => setModalSansPrenom(null)}
          onValidate={handleValidateModalSansPrenom}
        />
      )}
      {showModalFinTraitementHubee && (
        <AssistantModalFinTraitementHubee
          partenaire1={pacs?.conventionDeclaration?.partenaire1}
          partenaire2={pacs?.conventionDeclaration?.partenaire2}
          onConfirm={handleConfirmFinTraitementHubee}
          onCancel={() => setShowModalFinTraitementHubee(false)}
        />
      )}
      {showModalErrorStepFin && (
        <AssistantModalErrorStepFin
          onClose={() => setShowModalErrorStepFin(false)}
          onValidate={handleValidateModalErrorStepFin}
        />
      )}
      {/* isNil et non pas truthy : Steps.TypeDeclaration vaut 0 */}
      {!_.isNil(showModalRetourEtape) && (
        <ConfirmationModal
          onClose={() => setShowModalRetourEtape(undefined)}
          onConfirm={handleValidateModalRetourEtape}
        />
      )}
      {showConfirmModal && (
        <ConfirmationModal
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmNavigation}
        />
      )}
    </>
  );
};

export default Assistant;
